import { SourcingService } from 'api-clients/monolith';
import { PropsWithChildren } from 'react';
import { FormattedMessage } from 'react-intl';

import { Typography } from '@/components/ui/typography';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { useHiringGoalIdParam } from '@/hooks/use-hiring-goal-id-param';
import { queryKeys } from '@/query-keys';

import { AggregateBreakdownHoverCard } from './aggregate-breakdown-hover-card';

interface AggregateSpendHoverCardProps extends PropsWithChildren {
  openingSlug: string;
}

export function AggregateSpendHoverCard({
  children,
  openingSlug,
}: AggregateSpendHoverCardProps) {
  const { hiringGoalId } = useHiringGoalIdParam();
  const { formatCurrency } = useCurrencySymbol();

  return (
    <AggregateBreakdownHoverCard
      query={{
        queryKey: [
          ...queryKeys.dashboard.aggregateSpend,
          openingSlug,
          hiringGoalId,
        ],
        queryFn: () =>
          SourcingService.getInternalApiSourcingOpeningsAggregateSpend(
            openingSlug,
            hiringGoalId,
          ),
        select: data => data.aggregate_spend.filter(opening => opening.spend),
      }}
      boundary="dashboard.aggregate-spend-hover-card"
      description={
        <FormattedMessage
          defaultMessage="This number is the aggregate of multiple openings within the hiring workflow"
          description="Explains why spend rolls up when openings are linked in WX"
        />
      }
      valueHeader={
        <FormattedMessage
          defaultMessage="Spend"
          description="Hover card: spend attributed to a linked opening"
        />
      }
      renderRow={opening => (
        <>
          <Typography variant="body1" className="!text-fx-neutral-600 w-[90%]">
            {opening.name}
          </Typography>
          <Typography variant="body2" className="!text-fx-neutral-600">
            {formatCurrency(opening.spend ?? 0)}
          </Typography>
        </>
      )}
    >
      {children}
    </AggregateBreakdownHoverCard>
  );
}
